import {useCallback, useEffect, useRef, useState} from "react";
import {createDocumentCaptureSessionAPI} from "../../network/publicCalls/POSTDocumentCaptureSession";
import {getDocumentCaptureSessionAPI} from "../../network/publicCalls/GETDocumentCaptureSession";
import {getDocumentCaptureResultAPI} from "../../network/publicCalls/GETDocumentCaptureResult";
import {cancelDocumentCaptureSessionAPI} from "../../network/publicCalls/DELETEDocumentCaptureSession";
import {
    DOCUMENT_CAPTURE_POLL_INTERVAL_MS,
    DOCUMENT_CAPTURE_QR_SIZE,
    DOCUMENT_CAPTURE_REQUESTED_BY,
    DOCUMENT_CAPTURE_SESSION_TTL_MINUTES,
    isDocumentCaptureSessionClosed,
    isDocumentCaptureSessionReady,
} from "../../config/documentCapture";

export const DOCUMENT_CAPTURE_PHASE_IDLE = "idle";
export const DOCUMENT_CAPTURE_PHASE_CREATING = "creating";
export const DOCUMENT_CAPTURE_PHASE_WAITING = "waiting";
export const DOCUMENT_CAPTURE_PHASE_READY = "ready";
export const DOCUMENT_CAPTURE_PHASE_DELIVERED = "delivered";
export const DOCUMENT_CAPTURE_PHASE_ERROR = "error";

const POLL_ERROR_THRESHOLD = 3;
const CREATE_ERROR_MESSAGE = "Documentcontrole kon niet worden gestart. Probeer het opnieuw.";
const EXPIRED_ERROR_MESSAGE = "De documentcontrole is verlopen. Start een nieuwe controle om verder te gaan.";
const CANCELLED_ERROR_MESSAGE = "De documentcontrole is geannuleerd. Start een nieuwe controle om verder te gaan.";
const RESULT_ERROR_MESSAGE = "Het resultaat van de documentcontrole kon niet worden opgehaald. Start een nieuwe controle.";
const POLL_WARNING_MESSAGE = "Statuscontrole is tijdelijk niet beschikbaar. We blijven het automatisch proberen.";

/**
 * Owns the lifecycle of one Document Capture session: create → poll status →
 * fetch result. The result token never leaves this hook's state (it is not put
 * in the URL, storage or analytics).
 */
const useDocumentCaptureSession = ({subsidiaryId, active, resolveReference}) => {
    const [session, setSession] = useState(null);
    const [sessionStatus, setSessionStatus] = useState(null);
    const [phase, setPhase] = useState(DOCUMENT_CAPTURE_PHASE_IDLE);
    const [error, setError] = useState("");
    const [pollWarning, setPollWarning] = useState("");
    const [result, setResult] = useState(null);

    const sessionRef = useRef(null);
    const startPromiseRef = useRef(null);
    const resultPromiseRef = useRef(null);
    const pollNowRef = useRef(null);
    // Incremented on reset so a session created by an abandoned start() is discarded.
    const generationRef = useRef(0);

    const cancelCurrentSession = useCallback(async () => {
        const current = sessionRef.current;
        sessionRef.current = null;
        if (!current?.result_token || !subsidiaryId) return;
        try {
            await cancelDocumentCaptureSessionAPI({
                subsidiaryId,
                resultToken: current.result_token,
            });
        } catch (_error) {
            // Best effort: the session expires on its own when it cannot be cancelled.
        }
    }, [subsidiaryId]);

    const start = useCallback(async () => {
        if (!subsidiaryId) return null;
        if (startPromiseRef.current) return startPromiseRef.current;

        startPromiseRef.current = (async () => {
            await cancelCurrentSession();
            resultPromiseRef.current = null;
            const generation = generationRef.current;

            setPhase(DOCUMENT_CAPTURE_PHASE_CREATING);
            setError("");
            setPollWarning("");
            setResult(null);
            setSessionStatus(null);
            setSession(null);

            try {
                const reference = typeof resolveReference === "function"
                    ? await resolveReference()
                    : "";
                const response = await createDocumentCaptureSessionAPI({
                    subsidiaryId,
                    documents: ["drivers_license"],
                    selfie: true,
                    reference,
                    requestedBy: DOCUMENT_CAPTURE_REQUESTED_BY,
                    ttlMinutes: DOCUMENT_CAPTURE_SESSION_TTL_MINUTES,
                    qrSize: DOCUMENT_CAPTURE_QR_SIZE,
                    locale: "nl",
                });

                if (!response?.url || !response?.result_token) {
                    throw new Error("document_capture_session_incomplete");
                }

                if (generation !== generationRef.current) {
                    sessionRef.current = response;
                    await cancelCurrentSession();
                    return null;
                }

                sessionRef.current = response;
                setSession(response);
                setSessionStatus(response);
                setPhase(DOCUMENT_CAPTURE_PHASE_WAITING);
                return response;
            } catch (_error) {
                if (generation !== generationRef.current) return null;
                setPhase(DOCUMENT_CAPTURE_PHASE_ERROR);
                setError(CREATE_ERROR_MESSAGE);
                return null;
            } finally {
                startPromiseRef.current = null;
            }
        })();

        return startPromiseRef.current;
    }, [cancelCurrentSession, resolveReference, subsidiaryId]);

    const reset = useCallback(() => {
        generationRef.current += 1;
        cancelCurrentSession();
        resultPromiseRef.current = null;
        setSession(null);
        setSessionStatus(null);
        setResult(null);
        setError("");
        setPollWarning("");
        setPhase(DOCUMENT_CAPTURE_PHASE_IDLE);
    }, [cancelCurrentSession]);

    const fail = useCallback((message) => {
        sessionRef.current = null;
        resultPromiseRef.current = null;
        setResult(null);
        setPollWarning("");
        setError(message || RESULT_ERROR_MESSAGE);
        setPhase(DOCUMENT_CAPTURE_PHASE_ERROR);
    }, []);

    useEffect(() => {
        if (!active || phase !== DOCUMENT_CAPTURE_PHASE_WAITING) return undefined;
        const resultToken = session?.result_token;
        if (!resultToken || !subsidiaryId) return undefined;

        let cancelled = false;
        let inFlight = false;
        let consecutiveErrors = 0;

        const poll = async () => {
            if (cancelled || inFlight) return;
            inFlight = true;
            try {
                const status = await getDocumentCaptureSessionAPI({subsidiaryId, resultToken});
                if (cancelled) return;

                consecutiveErrors = 0;
                setPollWarning("");
                setSessionStatus(status);

                if (isDocumentCaptureSessionClosed(status)) {
                    sessionRef.current = null;
                    setPhase(DOCUMENT_CAPTURE_PHASE_ERROR);
                    setError(String(status?.status).toLowerCase() === "cancelled" ? CANCELLED_ERROR_MESSAGE : EXPIRED_ERROR_MESSAGE);
                    return;
                }

                if (isDocumentCaptureSessionReady(status)) {
                    setPhase(DOCUMENT_CAPTURE_PHASE_READY);
                }
            } catch (_error) {
                if (cancelled) return;
                consecutiveErrors += 1;
                if (consecutiveErrors >= POLL_ERROR_THRESHOLD) {
                    setPollWarning(POLL_WARNING_MESSAGE);
                }
            } finally {
                inFlight = false;
            }
        };

        pollNowRef.current = poll;
        poll();
        const intervalId = setInterval(poll, DOCUMENT_CAPTURE_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            if (pollNowRef.current === poll) {
                pollNowRef.current = null;
            }
        };
    }, [active, phase, session, subsidiaryId]);

    useEffect(() => {
        if (phase !== DOCUMENT_CAPTURE_PHASE_READY) return undefined;
        const resultToken = session?.result_token;
        if (!resultToken || !subsidiaryId) return undefined;

        // The first successful /result call closes the capture link, so the
        // request is shared between effect runs (React StrictMode re-runs effects).
        if (!resultPromiseRef.current || resultPromiseRef.current.resultToken !== resultToken) {
            resultPromiseRef.current = {
                resultToken,
                promise: getDocumentCaptureResultAPI({subsidiaryId, resultToken}),
            };
        }

        let cancelled = false;

        resultPromiseRef.current.promise
            .then((response) => {
                if (cancelled) return;
                sessionRef.current = null;
                setResult(response);
                setPhase(DOCUMENT_CAPTURE_PHASE_DELIVERED);
            })
            .catch(() => {
                if (cancelled) return;
                setPhase(DOCUMENT_CAPTURE_PHASE_ERROR);
                setError(RESULT_ERROR_MESSAGE);
            });

        return () => {
            cancelled = true;
        };
    }, [phase, session, subsidiaryId]);

    const pollNow = useCallback(() => {
        if (typeof pollNowRef.current === "function") {
            pollNowRef.current();
        }
    }, []);

    return {
        session,
        sessionStatus,
        phase,
        error,
        pollWarning,
        result,
        start,
        reset,
        fail,
        pollNow,
    };
};

export default useDocumentCaptureSession;
